import { serverTimestamp, Timestamp } from "firebase/firestore";
import React, { useCallback, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { useAppAuthState } from "../../hooks/use-app-auth-state";
import { createPodcast } from "../../services/podcastService";
import { Podcast } from "../../types/Podcast";
import "./ListPodcastModal.scss";

interface ListPodcastModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (podcast: Podcast) => void;
}

const ListPodcastModal: React.FC<ListPodcastModalProps> = ({
  show,
  onHide,
  onSuccess,
}) => {
  const { isLoggedIn, isAuthLoading, fireUser } = useAppAuthState();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [url, setUrl] = useState("");
  const [participants, setParticipants] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = useCallback(
    async (event: React.FormEvent) => {
      if (!fireUser) {
        setError("Please login to list a podcast.");
        return;
      }

      if (!title.trim()) {
        setError("Title is required.");
        return;
      }

      if (!url.trim()) {
        setError("URL is required.");
        return;
      }

      event.preventDefault();
      setError(null);
      setIsSubmitting(true);

      try {
        // Parse participants from comma-separated string
        const participantsList = participants
          .split(",")
          .map((p) => p.trim())
          .filter((p) => p.length > 0);

        const podcastData: Omit<Podcast, "id" | "userVotes"> = {
          title: title.trim(),
          description: description.trim() || null,
          imageUrl: imageUrl.trim() || null,
          url: url.trim(),
          participants: participantsList,
          createdAt: serverTimestamp() as Timestamp,
          listedBy: fireUser.uid,
        };
        const podcast = await createPodcast(podcastData);
        onSuccess(podcast);

        // Reset form
        setTitle("");
        setDescription("");
        setImageUrl("");
        setUrl("");
        setParticipants("");
        onHide();
      } catch (err) {
        setError("Failed to submit podcast. Please try again.");
        console.error("Submit error:", err);
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      description,
      fireUser,
      imageUrl,
      onHide,
      onSuccess,
      participants,
      title,
      url,
    ],
  );

  const handleClose = () => {
    if (!isSubmitting) {
      setError(null);
      onHide();
    }
  };

  // Show loading state while checking authentication
  if (isAuthLoading) {
    return null;
  }

  // Don't show modal if user is not authenticated
  if (!isLoggedIn) {
    return null;
  }

  return (
    <Modal
      className="basic-modal"
      show={show}
      onHide={handleClose}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>List a New Podcast</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          {error && <Alert variant="danger">{error}</Alert>}

          <Form.Group className="mb-3" controlId="formPodcastTitle">
            <Form.Label>Title</Form.Label>
            <Form.Control
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formPodcastDescription">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </Form.Group>

          <Form.Group className="mb-3" controlId="formPodcastUrl">
            <Form.Label>Podcast URL or Channel</Form.Label>
            <Form.Control
              type="url"
              placeholder="For example: https://www.youtube.com/@a16z"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
            />
            <Form.Text className="text-muted">
              Link to the podcast website or streaming platform
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3" controlId="formPodcastParticipants">
            <Form.Label>Participants</Form.Label>
            <Form.Control
              type="text"
              value={participants}
              onChange={(e) => setParticipants(e.target.value)}
            />
            <Form.Text className="text-muted">
              Comma-separated list of podcast hosts
            </Form.Text>
          </Form.Group>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={isSubmitting || !title.trim()}
        >
          {isSubmitting ? "Submitting..." : "List Podcast"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ListPodcastModal;
